/**
 * CS Resolutions (AI agent resolutions) — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "AI agent resolutions: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129370402
 *
 * Content DRI: Belle Craig · Meter CD: Blair Larkin
 *
 * NOTE ON PROFILE KEYS: The `UsageType` enum has stable keys
 * `exploring | adopting | scaling` for URL/state/analytics compatibility.
 * The CSR spec's visible labels are `Basic | Moderate | Comprehensive`,
 * which are the customer-facing strings shown on the profile cards.
 * The keys are internal identifiers only — do NOT rename them.
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 *
 * i18n: every copy field is wrapped in `defineMessage` with a literal
 * `id` and `defaultMessage`. The extractor is a static AST analyzer —
 * see the i18n contract in `meter-content.types.ts`. Keep both fields
 * literal; never build an id from a variable.
 */

import { defineMessage } from 'react-intl';

import type { MeterContent } from './meter-content.types';

export const CS_RESOLUTIONS_CONTENT: MeterContent = {
	description: defineMessage({
		id: 'ubp.calculator.usage.csResolutions.description',
		defaultMessage:
			'Use an AI agent to support customers and resolve requests before they reach your support team.',
	}),
	needsStep: {
		// Step 2 "Do you know your usage?" question — AI agent resolutions copy.
		doYouKnowQuestion: defineMessage({
			id: 'ubp.calculator.needs.csResolutions.doYouKnowQuestion',
			defaultMessage: 'Know how many resolutions you’ll need?',
		}),
	},
	estimateStep: {
		subtitle: defineMessage({
			id: 'ubp.calculator.estimate.csResolutions.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many AI Agent Resolutions you need, and how you can use them.',
		}),
		// CSR Step 3 doesn’t use the Compare-value flow — the CSR spec’s
		// Card 1 Primary CTA is “Get Customer Service Management” instead.
		// The dedicated `EstimateCsrStep` component renders that CTA. This
		// field is kept for shape parity with other meters and is unused by
		// `EstimateCsrStep`; if a future CSR variant reuses the ContentBlock
		// "Compare" pattern, the string here is ready to swap in.
		compareCta: defineMessage({
			id: 'ubp.calculator.estimate.csResolutions.compareCta',
			defaultMessage: 'Get Customer Service Management',
		}),
		// CSR's Compare button navigates to the public Service Collection
		// landing page rather than advancing to the calculator's Summary
		// step — CSR summary lives on that landing page instead. Per
		// AI agent resolutions Confluence spec and product direction.
		compareHref: 'https://www.atlassian.com/collections/service',
		// AI agent resolutions uses its own hero asset — the blue-panel
		// service-collection graphic from Figma node 1103:33298. Distinct
		// from the shared Rovo/Automations/Assets placeholder image.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/t7fyco15otc1im8640lqtcaa3uip5j53.png',
			alt: '',
		},
		// ContentBlock copy per AI agent resolutions Confluence spec —
		// Step 3 right-column "Get Service Collection" panel.
		contentBlock: {
			// Copy verbatim from AI agent resolutions Confluence spec Step 3
			// Card 1 (title "Get Customer Service Management to use AI agent
			// resolutions", body copy about proactive support, and the three
			// check-mark bullets underneath).
			title: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.contentBlock.title',
				defaultMessage: 'Get Customer Service Management to use AI agent resolutions',
			}),
			intro: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.contentBlock.intro',
				defaultMessage:
					'Deliver proactive support with AI agents, shared customer context, and connected teams.',
			}),
			listItems: [
				defineMessage({
					id: 'ubp.calculator.estimate.csResolutions.contentBlock.listItems.0',
					defaultMessage: 'Resolve requests faster with AI agents.',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.csResolutions.contentBlock.listItems.1',
					defaultMessage: 'Give your support team full customer context instantly.',
				}),
				defineMessage({
					id: 'ubp.calculator.estimate.csResolutions.contentBlock.listItems.2',
					defaultMessage: 'Unite support and engineering teams together.',
				}),
			],
			// Card 1 Primary CTA per spec is "Get Customer Service Management" —
			// point to the CSM landing page.
			href: '/software/customer-service-management',
		},
		// No `extraResultsCard` — the "Monthly cost estimate" panel is
		// intentionally omitted from the Estimate summary screen for AI agent
		// resolutions.
	},
	allowanceStep: {
		// Step 4 primary CTA — "Compare" per the latest design direction
		// (all meters share this comparison-focused verb on this step).
		nextCta: defineMessage({
			id: 'ubp.calculator.allowance.csResolutions.nextCta',
			defaultMessage: 'Compare',
		}),
	},
	summaryStep: {
		// Step 5 header — per AI agent resolutions Confluence spec.
		title: defineMessage({
			id: 'ubp.calculator.summary.csResolutions.title',
			defaultMessage: 'Review usage estimate and compare',
		}),
		subtitle: defineMessage({
			id: 'ubp.calculator.summary.csResolutions.subtitle',
			defaultMessage:
				'Based on what you’ve told us, here’s a summary of how many AI Agent Resolutions you need, and how you can use them.',
		}),
		// Per Figma node 2055:13212: CSR shows the org needs card AND
		// a Monthly Cost Estimate card (instead of "What's covered").
		// `headline` is injected at render time by summary-step.tsx from
		// the live usage-range calculation, so it's omitted here.
		// `body` and `footerNote` are omitted until the content team
		// finalizes them (ResultsCard renders without them).
		orgNeedsCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.csResolutions.orgNeedsCard.eyebrow',
				defaultMessage: 'YOUR ORGANIZATION NEEDS',
			}),
			headlineSubtext: defineMessage({
				id: 'ubp.calculator.summary.csResolutions.orgNeedsCard.headlineSubtext',
				defaultMessage: 'AI agent resolutions/monthly',
			}),
		},
		// GWP-12277 / Figma nodes 2164:27044 (Step 3) & 2164:28256 (Step 5):
		// Monthly Cost Estimate card. Renders on both Step 3 (Estimate) and
		// Step 5 (Summary) in the left column below the OrgNeedsCard. Body
		// copy is dynamic:
		//   - overage case  → `body`         (usage exceeds allowance)
		//   - covered case  → `bodyCovered`  (usage fits within allowance)
		// `{rate}` in `rateNote` is replaced at render time with the
		// currency-formatted per-resolution overage rate. `listItems` are
		// the AI agent resolutions value props from Confluence (Chat / Voice
		// / Email AI). Shown on Step 5 (Summary) only — Step 3's
		// MonthlyCostEstimateCard intentionally omits these bullets.
		overageCostCard: {
			eyebrow: defineMessage({
				id: 'ubp.calculator.summary.csResolutions.overageCostCard.eyebrow',
				defaultMessage: 'MONTHLY COST ESTIMATE',
			}),
			rateNote: defineMessage({
				id: 'ubp.calculator.summary.csResolutions.overageCostCard.rateNote',
				defaultMessage: 'at {rate} per resolution',
			}),
			body: defineMessage({
				id: 'ubp.calculator.summary.csResolutions.overageCostCard.body',
				defaultMessage: 'Your AI agent resolutions usage costs are not 100% covered by allowances.',
			}),
			bodyCovered: defineMessage({
				id: 'ubp.calculator.summary.csResolutions.overageCostCard.bodyCovered',
				defaultMessage: 'Your AI agent resolutions usage is covered by allowances.',
			}),
			listItems: [
				defineMessage({
					id: 'ubp.calculator.summary.csResolutions.overageCostCard.listItems.0',
					defaultMessage: 'Chat widget AI responds to customers instantly through your website.',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.csResolutions.overageCostCard.listItems.1',
					defaultMessage:
						'Voice AI answers calls, resolves common questions, and reduces wait times.',
				}),
				defineMessage({
					id: 'ubp.calculator.summary.csResolutions.overageCostCard.listItems.2',
					defaultMessage: 'Email AI provides immediate responses to customer requests via email.',
				}),
			],
		},
	},
	usageProfiles: {
		exploring: {
			label: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.exploring.label',
				defaultMessage: 'Basic',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.exploring.description',
				defaultMessage:
					'You’re building out your knowledge base and expect your AI agent to be able to handle some basic requests.',
			}),
			// usageRange omitted per design — not shown on CSR Step 2 cards.
			// list omitted — CSR profile cards don't render bullets.
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.exploring.valueProp',
				defaultMessage: 'Expected resolution rate: 20–40%',
			}),
		},
		adopting: {
			label: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.adopting.label',
				defaultMessage: 'Moderate',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.adopting.description',
				defaultMessage:
					'Your knowledge base covers your most common topics and your AI agent will be able to handle a meaningful share of questions.',
			}),
			// usageRange omitted per design — not shown on CSR Step 2 cards.
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.adopting.valueProp',
				defaultMessage: 'Expected resolution rate: 40–60%',
			}),
		},
		scaling: {
			label: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.scaling.label',
				defaultMessage: 'Comprehensive',
			}),
			description: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.scaling.description',
				defaultMessage:
					'Your knowledge base is thorough, well-maintained, and your AI agent will be able to resolve most questions without help.',
			}),
			// usageRange omitted per design — not shown on CSR Step 2 cards.
			valueProp: defineMessage({
				id: 'ubp.calculator.needs.csResolutions.scaling.valueProp',
				defaultMessage: 'Expected resolution rate: 60–80%',
			}),
		},
	},
	valueProps: [
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.valueProps.0.title',
				defaultMessage: 'Chat widget AI',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.valueProps.0.description',
				defaultMessage:
					'Responds to customers instantly through a chat widget embedded on your website.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.valueProps.1.title',
				defaultMessage: 'Voice AI',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.valueProps.1.description',
				defaultMessage:
					'Answers calls, resolves common questions, reduces wait times and generates transcripts.',
			}),
		},
		{
			title: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.valueProps.2.title',
				defaultMessage: 'Email AI',
			}),
			description: defineMessage({
				id: 'ubp.calculator.estimate.csResolutions.valueProps.2.description',
				defaultMessage: 'Provides immediate responses to customer requests sent via email.',
			}),
		},
	],
};
